import { z } from 'zod';
import US_STATES_WITH_INITIALS from './us_states_and_initials';

const LICENCE_PLATE_PATTERN = /^[A-Z0-9]+$/;

const unitPermitSchema = z.object({
  licence_plate: z
    .string()
    .min(1, { message: 'License plate is required.' })
    .max(11, { message: 'License plate must be 11 or less characters' })
    .regex(LICENCE_PLATE_PATTERN, {
      message:
        'License plate must be only capital numbers and letters without especial chars',
    }),
  licence_state: z
    .string()
    .min(1, { message: 'License State is required.' })
    .refine(
      (value) => {
        const code = String(value).trim().toUpperCase();
        return Boolean(US_STATES_WITH_INITIALS[code]);
      },
      { message: 'Invalid License State' }
    ),
  make: z.string().min(1, { message: 'Make is required.' }),
  model: z.string().min(1, { message: 'Model is required.' }),
  color: z.string().min(1, { message: 'Color is required.' }),
  expiration_date: z
    .any()
    .refine((value) => value !== null && value !== undefined && value !== '', {
      message: 'Expiration date is required.',
    }),
});

const PERMIT_FIELD_KEYS = [
  'licence_plate',
  'licence_state',
  'make',
  'model',
  'color',
  'status',
  'expiration_date',
  'issue_date',
];

const formatPermitFieldErrors = (vehiclesKey, vehicleIndex, err) => {
  const vehicleErrors = {};

  err.errors.forEach((validationError) => {
    const field = validationError.path[0];

    if (!field) {
      return;
    }

    if (!vehicleErrors[field]) {
      vehicleErrors[field] = [];
    }

    vehicleErrors[field].push(validationError.message);
  });

  return { [vehiclesKey]: { [vehicleIndex]: vehicleErrors } };
};

/** Reshape flat or mismatched backend permit errors into nested fieldErrors shape. */
const reshapeBackendPermitErrors = (errorData, vehiclesKey, vehicleIndex) => {
  if (!errorData || typeof errorData !== 'object' || Array.isArray(errorData)) {
    return errorData;
  }

  if (errorData[vehiclesKey]) {
    return errorData;
  }

  const vehicleErrors = {};
  let hasField = false;

  PERMIT_FIELD_KEYS.forEach((key) => {
    if (errorData[key] === undefined) {
      return;
    }
    hasField = true;
    vehicleErrors[key] = Array.isArray(errorData[key])
      ? errorData[key]
      : [errorData[key]];
  });

  if (!hasField) {
    return errorData;
  }

  return { [vehiclesKey]: { [vehicleIndex]: vehicleErrors } };
};

const addUnitSchema = z.object({
  first_name: z.string().min(1, { message: 'First name is required.' }),
  last_name: z.string().min(1, { message: 'Last name is required.' }),
  email: z
    .string()
    .min(1, { message: 'Email address is required.' })
    .email({ message: 'Email address is not in the right format.' }),
  phone_number: z.string().min(1, { message: 'Phone number is required.' }),
  unit_number: z.string().min(1, { message: 'Unit number is required.' }),
});

const editTenantSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'Email address is required.' })
    .email({ message: 'Email address is not in the right format.' }),
  phone_number: z.string().min(1, { message: 'Phone number is required.' }),
});

const resendActivationLinkSchema = z.object({
  first_name: z.string().min(1, { message: 'First name is required.' }),
  last_name: z.string().min(1, { message: 'Last name is required.' }),
  email: z
    .string()
    .min(1, { message: 'Email address is required.' })
    .email({ message: 'Email address is not in the right format.' }),
  phone_number: z.string().min(1, { message: 'Phone number is required.' }),
});

export {
  addUnitSchema,
  editTenantSchema,
  resendActivationLinkSchema,
  unitPermitSchema,
  formatPermitFieldErrors,
  reshapeBackendPermitErrors,
};
